/**
 * A real CSV reader, because `split(',')` cannot read a Flightradar24 export.
 *
 * FR24 writes position as one quoted field containing a comma:
 *
 *     1789507694,2026-09-15T21:28:14Z,ZKDFH,"-37.923626,176.918381",0,0,8
 *
 * Splitting on commas gives eight fields for a seven-column row and shifts
 * altitude, speed and heading one place left — which looks like plausible data
 * and is entirely wrong.
 *
 * RFC 4180 with the tolerances real exports need: CRLF or LF, a doubled quote
 * inside a quoted field, a trailing newline, and an unterminated final quote
 * rather than an exception.
 */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  let fieldStarted = false;

  // A UTF-8 BOM would otherwise become part of the first header name, and the
  // header map would fail to find a column that is plainly there.
  const input = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;

  const endField = (): void => {
    row.push(field);
    field = '';
    fieldStarted = false;
  };
  const endRow = (): void => {
    endField();
    // A blank line is not a row. FR24 ends its files with one.
    if (row.length > 1 || (row[0] ?? '') !== '') {
      rows.push(row);
    }
    row = [];
  };

  for (let i = 0; i < input.length; i += 1) {
    // `charAt` rather than `input[i]`: the index is provably in range, and
    // `charAt` says so in the type instead of making every concatenation below
    // reason about an `undefined` that cannot occur.
    const char = input.charAt(i);

    if (quoted) {
      if (char === '"') {
        if (input[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        field += char;
      }
      continue;
    }

    switch (char) {
      case '"':
        // A quote only opens a field at its start; mid-field it is literal,
        // which is what a stray inch mark in a remarks column looks like.
        if (fieldStarted) {
          field += char;
        } else {
          quoted = true;
          fieldStarted = true;
        }
        break;
      case ',':
        endField();
        break;
      case '\r':
        // Swallow CR only as part of CRLF; a lone CR is a line break too.
        if (input[i + 1] === '\n') {
          i += 1;
        }
        endRow();
        break;
      case '\n':
        endRow();
        break;
      default:
        field += char;
        fieldStarted = true;
        break;
    }
  }

  if (field !== '' || row.length > 0) {
    endRow();
  }

  return rows;
}

/** Quotes a field only when it needs it — a comma, quote or line break inside it, or leading/trailing whitespace a naive reader would trim. */
function quoteCsvField(value: string): string {
  if (/[",\r\n]|^\s|\s$/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * The write side of `parseCsv`: RFC 4180, CRLF line endings (the format's own
 * wire convention, and what Excel expects without a "which encoding" prompt).
 * Every logbook interchange export goes through this rather than `join(',')`,
 * for the same reason `parseCsv` exists — a remarks column with a comma or a
 * quote in it is routine, not an edge case.
 */
export function stringifyCsv(rows: readonly (readonly (string | number)[])[]): string {
  return rows.map((row) => row.map((cell) => quoteCsvField(String(cell))).join(',')).join('\r\n');
}

/**
 * Normalise a header cell so that `Altitude`, `altitude`, `Altitude (ft)` and
 * `ALT_FT` all collapse to something a synonym table can match.
 */
export function normaliseHeader(header: string): string {
  return header
    .toLowerCase()
    .replace(/\(.*?\)/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Resolve named columns against a header row.
 *
 * Column *order* is never assumed — the plan's rule is to match on names and
 * keep a synonym map, so that a reordered or renamed export still reads, and a
 * genuinely unknown layout fails with the names it did find rather than with
 * silently shifted numbers.
 *
 * @param headerRow  the file's first row.
 * @param synonyms   field name to the normalised header spellings it accepts,
 *                   best first.
 * @returns a column index per field; `-1` where nothing matched.
 */
export function mapColumns<F extends string>(
  headerRow: readonly string[],
  synonyms: Readonly<Record<F, readonly string[]>>,
): Record<F, number> {
  const normalised = headerRow.map(normaliseHeader);
  const result = {} as Record<F, number>;

  for (const field of Object.keys(synonyms) as F[]) {
    let index = -1;
    for (const candidate of synonyms[field]) {
      index = normalised.indexOf(candidate);
      if (index !== -1) {
        break;
      }
    }
    // Fall back to a prefix match: `altitude ft` is already covered by stripping
    // the bracketed unit, but `altitudeft` and `altitude_feet` are not.
    if (index === -1) {
      for (const candidate of synonyms[field]) {
        index = normalised.findIndex(
          (header) => header.replace(/ /g, '') === candidate.replace(/ /g, ''),
        );
        if (index !== -1) {
          break;
        }
      }
    }
    result[field] = index;
  }

  return result;
}
